import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { DeviceSoftwarePackageFirmwareConstraintPost } from "../../../../models/DeviceSoftwarePackageFirmwareConstraint"
import { createDeviceSoftwarePackageFirmwareConstraintValidationSchema } from "../../../../components/Helpers"
import { ControllerRendererProps } from "../../../../types/reactHookForm"

const validationSchema = createDeviceSoftwarePackageFirmwareConstraintValidationSchema()

export function DeviceSoftwarePackageFirmwareConstraintCreate() {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const returnURL = `/devices/${deviceId}#software-package-firmware-constraints`

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<DeviceSoftwarePackageFirmwareConstraintPost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      softwarePackageEditionCode: "",
      mainboardMinSoftwareVersion: "",
      hmiMinSoftwareVersion: "",
    })
  }, [reset])

  const onSubmit = (values: DeviceSoftwarePackageFirmwareConstraintPost) => digitalManagerApi
      .post(`/devices/${deviceId}/softwarePackageFirmwareConstraints`, values)
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Software Package Firmware Constraint created")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot create software Package Firmware Constraint")
      })

  const handleTextFieldChange = useCallback(
    (
      field: ControllerRenderProps<
        DeviceSoftwarePackageFirmwareConstraintPost,
        | "softwarePackageEditionCode"
        | "mainboardMinSoftwareVersion"
        | "hmiMinSoftwareVersion"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderSoftwarePackageEditionCodeTextField = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      DeviceSoftwarePackageFirmwareConstraintPost,
      "softwarePackageEditionCode"
    >) => (
      <MUITextField
        id="softwarePackageEditionCode"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        errors={
          errors.softwarePackageEditionCode?.message
            ? {
                softwarePackageEditionCode: {
                  message: errors.softwarePackageEditionCode.message,
                },
              }
            : undefined
        }
      />
    ),
    [errors.softwarePackageEditionCode?.message, handleTextFieldChange]
  )

  const renderMainboardMinSoftwareVersionTextField = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      DeviceSoftwarePackageFirmwareConstraintPost,
      "mainboardMinSoftwareVersion"
    >) => (
      <MUITextField
        id="mainboardMinSoftwareVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        errors={
          errors.mainboardMinSoftwareVersion?.message
            ? {
                mainboardMinSoftwareVersion: {
                  message: errors.mainboardMinSoftwareVersion.message,
                },
              }
            : undefined
        }
      />
    ),
    [errors.mainboardMinSoftwareVersion?.message, handleTextFieldChange]
  )

  const renderHmiMinSoftwareVersionTextField = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      DeviceSoftwarePackageFirmwareConstraintPost,
      "hmiMinSoftwareVersion"
    >) => (
      <MUITextField
        id="hmiMinSoftwareVersion"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        placeholder="XX.XX.XX"
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        errors={
          errors.hmiMinSoftwareVersion?.message
            ? { hmiMinSoftwareVersion: { message: errors.hmiMinSoftwareVersion.message } }
            : undefined
        }
      />
    ),
    [errors.hmiMinSoftwareVersion?.message, handleTextFieldChange]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Software Package Edition Code">
        <Controller
          name="softwarePackageEditionCode"
          control={control}
          render={renderSoftwarePackageEditionCodeTextField}
        />
      </FieldRow>

      <FieldRow label="Mainboard Min SW Version">
        <Controller
          name="mainboardMinSoftwareVersion"
          control={control}
          render={renderMainboardMinSoftwareVersionTextField}
        />
      </FieldRow>

      <FieldRow label="HMI Min SW Version">
        <Controller
          name="hmiMinSoftwareVersion"
          control={control}
          render={renderHmiMinSoftwareVersionTextField}
        />
      </FieldRow>
    </FormPage>
  )
}
